import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { debounce } from "../../../../../system";
import {
  Directions,
  genderEnumReversed,
  translationConstants,
  youthRadioConstants,
} from "../../../../../constants";

import { CancelIcon } from "../../../../../assets/images/icon";
import { SearchBoxIcon } from "../../../../../assets/images/icon/iconsProvider";
import { leaderboardActions } from "../../../../../redux/actions";
import { useLocation } from "react-router-dom";

const defaultQueryExpression = "";
const column = {
  age: "participantAge",
  search: "search",
  genderBoatClassId: "genderBoatClassId",
};

const FilterComponent = (props) => {
  const { gridState, setGridState } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const competitionResult = useSelector(
    (state) => state?.CompetitionReducer?.competitionViewResult
  );
  const competitionBoatClasses = useSelector((state) => {
    return state.CompetitionReducer.leaderboardBoatClasses.data;
  });
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );

  const searchInputRef = useRef(null);
  const [inputSearch, setInputSearch] = useState("");
  const [genderBoatClassFilter, setGenderBoatClassFilter] = useState("");
  const [youthFilter, setYouthFilter] = useState(youthRadioConstants.OVERALL);

  const buildFilterQuery = () => {
    let filterQueryExpression = defaultQueryExpression;

    if (genderBoatClassFilter) {
      filterQueryExpression += `${column.genderBoatClassId} == \"${genderBoatClassFilter}\"`;
    }

    if (youthFilter === youthRadioConstants.YOUTH_ONLY) {
      filterQueryExpression += filterQueryExpression
        ? ` && ${column.age} <= ${process.env.REACT_APP_YOUTH_ONLY_AGE}`
        : `${column.age} <= ${process.env.REACT_APP_YOUTH_ONLY_AGE}`;
    }

    return filterQueryExpression;
  };

  const onFilterChange = useCallback((key, value) => {
    switch (key) {
      case column.genderBoatClassId:
        setGenderBoatClassFilter(value);
        break;
      case column.age:
        setYouthFilter(value);
        break;
      case column.search:
        setInputSearch(value);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    const filterQueryExpression = buildFilterQuery();
    setGridState({
      ...gridState,
      filterQuery: filterQueryExpression,
      search: inputSearch,
    });
    // eslint-disable-next-line
  }, [inputSearch, youthFilter, genderBoatClassFilter]);

  useEffect(() => {
    if (genderBoatClassFilter) {
      dispatch(
        leaderboardActions.getCompetitionRanking(
          gridState,
          location.state?.competition?.id
        )
      );
    }
    // eslint-disable-next-line
  }, [gridState]);

  const handleResetSearch = () => {
    setInputSearch("");
    searchInputRef.current.value = "";
  };

  const handleClearFilters = () => {
    setYouthFilter(youthRadioConstants.OVERALL);
    setGridState({
      ...gridState,
      filterQuery: defaultQueryExpression,
    });
  };

  const handleSearch = debounce((value) => {
    setInputSearch(value);
  }, process.env.REACT_APP_SEARCH_DEBOUNCE_INTERVAL);

  const boatClassOptions = useMemo(() => {
    if (!competitionBoatClasses) return null;

    setGenderBoatClassFilter(
      competitionBoatClasses?.length > 0 ? competitionBoatClasses[0]?.id : ""
    );

    return competitionBoatClasses?.map((object) => {
      const translatedGender = t(
        translationConstants[genderEnumReversed[object.gender]]
      );
      const name =
        direction === Directions.rtl && object.arabicName
          ? object.arabicName
          : object.name;
      return (
        <option key={object.id} value={object.id}>
          {`${name} - ${translatedGender}`}
        </option>
      );
    });
  }, [competitionBoatClasses, direction]);

  return (
    <Row className="">
      <Col md={4} xs={12} className="px-2 my-2 my-md-0">
        <h6 className="font-14">
          {t(translationConstants.SEARCH_ANY_KEYWORD)}
        </h6>
        <div className="d-flex justify-content-sm-start justify-content-center">
          <div className="searchInputDiv gap-2 d-flex justify-content-between align-items-center w-100">
            <div>
              <SearchBoxIcon />
            </div>
            <input
              type="text"
              className="searchInput w-100"
              placeholder={t(translationConstants.Search)}
              onChange={(e) => handleSearch(e.target.value)}
              ref={searchInputRef}
            />
            {inputSearch && (
              <div onClick={handleResetSearch} className="cross cursor-pointer">
                <img width={16} src={CancelIcon} alt="cancel icon" />
              </div>
            )}
          </div>
        </div>
      </Col>

      <Col md={3} xs={12} className="px-2 my-2 my-md-0">
        <h6 className="font-14">{t(translationConstants.SELECT_BOAT_CLASS)}</h6>
        <Form.Select
          className="custom-select-icon filter-style"
          value={genderBoatClassFilter}
          onChange={(event) =>
            onFilterChange(column.genderBoatClassId, event.target.value)
          }
        >
          {boatClassOptions?.length > 0 ? (
            boatClassOptions
          ) : (
            <option disabled value="">
              {t(translationConstants.NO_OPTION_AVAILABLE)}
            </option>
          )}
        </Form.Select>
      </Col>

      <Col
        xs={12}
        md={2}
        className="px-2 my-2 my-md-0 d-flex flex-md-column justify-content-md-end align-items-start flex-row gap-2 gap-md-0"
      >
        <Form.Check
          type="radio"
          name="radioGroup"
          id="overall"
          label={t(translationConstants.OVERALL)}
          value={youthRadioConstants.OVERALL}
          checked={youthFilter === youthRadioConstants.OVERALL}
          onChange={(event) => {
            onFilterChange(column.age, event.target.value);
          }}
        />
        <Form.Check
          type="radio"
          name="radioGroup"
          id="youthOnly"
          label={t(translationConstants.YOUTH_ONLY)}
          value={youthRadioConstants.YOUTH_ONLY}
          checked={youthFilter === youthRadioConstants.YOUTH_ONLY}
          onChange={(event) => {
            onFilterChange(column.age, event.target.value);
          }}
        />
      </Col>

      {youthFilter === youthRadioConstants.YOUTH_ONLY && (
        <Col
          xs={12}
          md="auto"
          className={`${
            competitionResult?.document?.link || competitionResult?.link
              ? "d-md-none"
              : "d-flex"
          } px-2 flex-md-column justify-content-center justify-content-md-end align-items-start`}
        >
          <div className="cancel-filter" onClick={handleClearFilters}>
            {t(translationConstants.RESET_FIILTERS)}
          </div>
        </Col>
      )}

      {(competitionResult?.document?.link || competitionResult?.link) && (
        <Col
          xs={12}
          md={2}
          className="px-2 d-flex flex-md-column justify-content-center justify-content-md-end align-items-start"
        >
          <span
            className="center-card-view-detail-link approved-color"
            onClick={() => {
              window.open(
                competitionResult?.document?.link
                  ? competitionResult?.document?.link
                  : competitionResult?.link
              );
            }}
          >
            {t(translationConstants.VIEW_DETAILS_RESULT)}
          </span>
        </Col>
      )}

      {youthFilter === youthRadioConstants.YOUTH_ONLY && (
        <Col
          xs={12}
          className={`${
            competitionResult?.document?.link || competitionResult?.link
              ? "d-none d-md-flex"
              : "d-none"
          } px-4 flex-md-column justify-content-center justify-content-md-end align-items-end`}
        >
          <div className="cancel-filter" onClick={handleClearFilters}>
            {t(translationConstants.RESET_FIILTERS)}
          </div>
        </Col>
      )}
    </Row>
  );
};

export default FilterComponent;
